// components/shared/ErrorBoundary.tsx
import React, { Component, type ComponentType, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronRight } from 'lucide-react';

export interface ErrorBoundaryProps {
  children?: ReactNode;
  /**
   * Optional callback fired when the user clicks "Try Again". Use it for
   * side-effects like clearing localStorage or refetching data; the
   * boundary already resets its own error state.
   */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showDetails: boolean;
  errorCount: number;
}

/**
 * Top-level error boundary. Catches errors in any descendant component tree
 * and renders a recovery UI.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState((prevState) => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = (): void => {
    window.location.reload();
  };

  toggleDetails = (): void => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails, errorCount } = this.state;
      const isDevelopment = process.env.NODE_ENV === 'development';

      if (errorCount > 3) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
              <div className="bg-white rounded-lg shadow-lg border border-red-200 p-6 text-center">
                <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h1 className="text-xl font-semibold text-gray-900 mb-2">
                  Multiple Errors Detected
                </h1>
                <p className="text-gray-600 mb-6">
                  The application is experiencing repeated errors. Please reload the page.
                </p>
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-800 transition-colors"
                >
                  Reload Application
                </button>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="bg-white rounded-lg shadow-lg border border-red-200 overflow-hidden">
              <div className="bg-red-50 border-b border-red-200 p-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 mb-1">
                      Oops! Something went wrong
                    </h1>
                    <p className="text-gray-600">
                      {isDevelopment
                        ? 'An error occurred while rendering this component'
                        : "We're sorry, but something unexpected happened. Please try refreshing the page."}
                    </p>
                  </div>
                </div>
              </div>

              {isDevelopment && error && (
                <div className="p-6 border-b border-gray-200">
                  <div className="bg-gray-900 text-white p-4 rounded-lg font-mono text-sm overflow-x-auto">
                    <div className="font-semibold text-red-400 mb-2">{error.toString()}</div>
                    {error.stack && (
                      <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                        {error.stack}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {isDevelopment && errorInfo && (
                <div className="border-b border-gray-200">
                  <button
                    onClick={this.toggleDetails}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-700">Component Stack Trace</span>
                    {showDetails ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </button>

                  {showDetails && (
                    <div className="px-6 pb-4">
                      <pre className="bg-gray-100 p-4 rounded-lg text-xs font-mono overflow-x-auto">
                        {errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              <div className="p-6 bg-gray-50">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={this.handleReset}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-800 transition-colors flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </button>

                  <button
                    onClick={this.handleReload}
                    className="flex-1 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reload Page
                  </button>

                  <a
                    href="/"
                    className="flex-1 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Home className="h-4 w-4" />
                    Go Home
                  </a>
                </div>

                <div className="mt-4 text-sm text-gray-600 text-center">
                  <p>If this problem persists, please contact support or try again later.</p>
                  {isDevelopment && (
                    <p className="mt-2 text-xs">
                      Error ID: {Date.now()}-{Math.random().toString(36).substring(2, 11)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export interface SectionErrorBoundaryProps {
  children?: ReactNode;
  /** Optional custom fallback UI rendered instead of the default error card. */
  fallback?: ReactNode;
  title?: string;
  message?: string;
  onReset?: () => void;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Bounded error boundary for individual UI sections (e.g. a single column).
 * Doesn't replace the whole page on error.
 */
export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('SectionErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback, title = 'Something went wrong', message } = this.props;

      if (fallback) {
        return fallback;
      }

      return (
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800 mb-1">{title}</h3>
              <p className="text-sm text-red-600 mb-3">
                {message || "We couldn't load this section. Please try again."}
              </p>
              <button
                onClick={this.handleReset}
                className="text-sm px-3 py-1.5 bg-white text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC for wrapping components with a SectionErrorBoundary.
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: ComponentType<P>,
  errorBoundaryProps: SectionErrorBoundaryProps = {}
): ComponentType<P> {
  const Wrapped = (props: P) => (
    <SectionErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </SectionErrorBoundary>
  );

  Wrapped.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return Wrapped;
}

export default ErrorBoundary;
